// ============================================================
// AGRIDA — Centralized Number & Currency Formatters
// Single source of truth for all display formatting.
// ============================================================

// ─── Helpers ─────────────────────────────────────────────────

/** Internal: format integer with Vietnamese thousand separators (dots) */
function withSeparators(n: number): string {
  return n.toLocaleString('vi-VN');
}

/** Guard against NaN / Infinity / null / undefined */
function safeNum(v: number | null | undefined): number | null {
  if (v == null) return null;
  if (!Number.isFinite(v)) return null;
  return v;
}

// ─── Public API ──────────────────────────────────────────────

/**
 * Full currency: "5.000.000.000 đ" — only for detail views with enough space.
 * Returns "—" for null / NaN / Infinity.
 */
export function formatCurrencyFull(value: number | null | undefined): string {
  const n = safeNum(value);
  if (n === null) return '—';
  return `${withSeparators(n)} đ`;
}

/**
 * Compact currency for stat cards & mobile:
 *   >= 1B → "5 tỷ" / "6,238 tỷ"     (max 3 decimals)
 *   >= 1M → "300 triệu" / "50,5 triệu"
 *   >= 1K → "500 nghìn"
 *   < 1K  → "999"
 * Returns "—" for null / NaN / Infinity.
 */
export function formatCurrencyCompact(value: number | null | undefined): string {
  const n = safeNum(value);
  if (n === null) return '—';

  const absN = Math.abs(n);
  const sign = n < 0 ? '-' : '';

  if (absN >= 1_000_000_000) {
    const v = absN / 1_000_000_000;
    return `${sign}${formatCompactDecimal(v)} tỷ`;
  }
  if (absN >= 1_000_000) {
    const v = absN / 1_000_000;
    return `${sign}${formatCompactDecimal(v)} triệu`;
  }
  if (absN >= 1_000) {
    const v = absN / 1_000;
    return `${sign}${formatCompactDecimal(v)} nghìn`;
  }
  return sign + withSeparators(absN);
}

function formatCompactDecimal(value: number): string {
  return value.toLocaleString('vi-VN', { maximumFractionDigits: 3 });
}

/**
 * Plain number with thousand separators: "1.200.000"
 * Returns "—" for null / NaN.
 */
export function formatNumber(value: number | null | undefined): string {
  const n = safeNum(value);
  if (n === null) return '—';
  return withSeparators(n);
}

/**
 * Percentage: "68%" — always rounded to 1 decimal.
 * Returns "—" for null / NaN / Infinity.
 */
export function formatPercent(value: number | null | undefined): string {
  const n = safeNum(value);
  if (n === null) return '—';
  return `${Math.round(n * 10) / 10}%`;
}

/**
 * Date: dd/mm/yyyy
 * Returns '' for empty/invalid input.
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * DateTime: dd/mm/yyyy HH:MM
 * Returns '' for empty/invalid input.
 */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${h}:${m}`;
}

/**
 * Today's date as YYYY-MM-DD (for <input type="date">)
 */
export function todayInputValue(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Compact currency with 4 decimals for billions (e.g. "3,4575 tỷ").
 * For millions: 1 decimal. For smaller: full number with separators + " đ".
 * Returns "—" for null / NaN / Infinity.
 */
export function formatCurrencyCompact4(value: number | null | undefined): string {
  const n = safeNum(value);
  if (n === null) return '—';

  const absN = Math.abs(n);
  const sign = n < 0 ? '-' : '';

  if (absN >= 1_000_000_000) {
    const v = (absN / 1_000_000_000).toFixed(4);
    const parts = v.split('.');
    return sign + parts[0] + ',' + parts[1] + ' tỷ';
  }
  if (absN >= 1_000_000) {
    const v = absN / 1_000_000;
    if (v % 1 === 0) return sign + v + ' triệu';
    return sign + v.toFixed(1).replace('.', ',') + ' triệu';
  }
  return sign + withSeparators(absN) + ' đ';
}

// ─── Progress / Usage ────────────────────────────────────────

/**
 * Safe usage percentage: (used / total) * 100
 * Returns 0 when total is 0 or invalid.
 * Clamped to 0–100 by default (pass clamp=false to disable).
 */
export function calculateUsagePercent(
  used: number | null | undefined,
  total: number | null | undefined,
  clamp: boolean = true,
): number {
  const u = safeNum(used) ?? 0;
  const t = safeNum(total) ?? 0;
  if (t === 0) return 0;
  const raw = (u / t) * 100;
  if (!clamp) return Math.round(raw * 10) / 10;
  return Math.max(0, Math.min(100, Math.round(raw * 10) / 10));
}

/**
 * Progress bar + text color based on usage percentage.
 *   0–60%  → green (an toàn)
 *   61–80% → amber (cảnh báo nhẹ)
 *   81–100% → red (cảnh báo)
 *   >100%   → red + label "Vượt hạn mức"
 */
export function getProgressColor(percent: number): {
  barColor: string;
  textColor: string;
  label: string | null;
} {
  if (percent > 100) {
    return {
      barColor: 'bg-red-500',
      textColor: 'text-red-700',
      label: 'Vượt hạn mức',
    };
  }
  if (percent >= 81) {
    return {
      barColor: 'bg-red-400',
      textColor: 'text-red-600',
      label: null,
    };
  }
  if (percent >= 61) {
    return {
      barColor: 'bg-amber-400',
      textColor: 'text-amber-600',
      label: null,
    };
  }
  return {
    barColor: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    label: null,
  };
}
